<template>
  <router-view />
  <NotifyToast ref="notifyRef" />
  <ConfirmDialog ref="confirmRef" />
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import NotifyToast from './components/NotifyToast.vue'
import ConfirmDialog from './components/ConfirmDialog.vue'
import { setNotifyRef, setConfirmRef } from './composables/useNotify'
import { useUserStore } from '@/stores/user'
import { startTokenRefreshTimer, stopTokenRefreshTimer } from '@/services/authService'

const notifyRef = ref(null)
const confirmRef = ref(null)
setNotifyRef(notifyRef)
setConfirmRef(confirmRef)

const userStore = useUserStore()
const route = useRoute()
const router = useRouter()

// 消费 GitHub OAuth 回调带回的参数：token 直接登录；error 在来源页提示错误
async function consumeOAuthCallback() {
  // 根组件 onMounted 可能早于初始导航完成，先确保路由就绪，否则 route.query 为空
  await router.isReady()
  const token = route.query.token
  const error = route.query.error
  if (!token && !error) return
  const cleanPath = route.path // 清理 URL，避免刷新重复消费/泄露
  try {
    if (typeof error === 'string') {
      notifyRef.value?.addToast(error, 'error')
    }
    if (typeof token === 'string') {
      userStore.setToken(token)
      startTokenRefreshTimer()
      await userStore.fetchUserInfo()
      // 从 /login 页发起（无来源 → 回调落首页）且是管理员 → 进后台，与账号密码登录行为一致
      if (route.path === '/' && userStore.isAdmin) {
        router.push('/admin')
      }
    }
  } finally {
    window.history.replaceState({}, '', cleanPath)
  }
}

const handleAuthExpired = () => {
  notifyRef.value?.addToast('登录已过期，请重新登录', 'warning')
  setTimeout(() => { window.location.href = '/login' }, 1500)
}

const handleAuthBanned = () => {
  notifyRef.value?.addToast('您的账号已被封禁', 'error')
  setTimeout(() => { window.location.href = '/login' }, 2000)
}

const handleNoPermission = () => {
  notifyRef.value?.addToast('无权限访问', 'warning')
}

onMounted(() => {
  window.addEventListener('auth:expired', handleAuthExpired)
  window.addEventListener('auth:banned', handleAuthBanned)
  window.addEventListener('auth:no-permission', handleNoPermission)
  // 兜底：路由守卫可能在 App 挂载前触发，通过 sessionStorage 传递
  const pendingMsg = sessionStorage.getItem('auth:notify')
  if (pendingMsg) {
    sessionStorage.removeItem('auth:notify')
    notifyRef.value?.addToast(pendingMsg, 'warning')
  }
  if (userStore.isLoggedIn) {
    startTokenRefreshTimer()
  }
  consumeOAuthCallback()
})

onUnmounted(() => {
  window.removeEventListener('auth:expired', handleAuthExpired)
  window.removeEventListener('auth:banned', handleAuthBanned)
  window.removeEventListener('auth:no-permission', handleNoPermission)
  stopTokenRefreshTimer()
})
</script>

<style>
* {
  box-sizing: border-box;
}

html,
body,
#app {
  margin: 0;
  height: 100%;
}

body {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

a {
  color: inherit;
  text-decoration: none;
}

button,
input,
select,
textarea {
  font: inherit;
}
</style>
